import AccountExists from "features/authentication/components/register/AccountExists";
import RegisterModal from "features/authentication/components/register/RegisterModal";
import SideImageSection from "features/authentication/components/SideImageSection.jsx";
import Button from "components/shared/button/Button.jsx";
import ErrorMessage from "components/shared/error-message/ErrorMessage.jsx";
import InputField from "components/shared/input-field/InputField.jsx";
import Pin from "components/shared/pin/Pin.jsx";
import Option from "components/shared/select-and-option/Option.jsx";
import Select from "components/shared/select-and-option/Select.jsx";
import { serverUrlInputFieldTooltip } from "config/constants/tooltips";
import { defaultServerUrl } from "config/constants/urls.js";
import { versions } from "config/constants/versions.js";
import ThemeContext from "config/context/themeContext";
import {
  useValidateAccount,
  useValidateIDBG,
  useValidateServer,
} from "features/authentication/hooks/useRegisterHook.js";
import { areAllElementsNumbers } from "lib/areAllElementsNumbers";
import { debounce, last } from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./Register.module.css";
import { initPinState } from "components/shared/pin/init-state";

const Register = () => {
  const { dark } = useContext(ThemeContext);
  const [pin, setPin] = useState(initPinState);
  const [serverURL, setServerURL] = useState(defaultServerUrl);
  const [account, setAccount] = useState("");
  const [idbg, setIdbg] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const widthRef = useRef(window.innerWidth);
  const [selectedVersion, setSelectedVersion] = useState(last(versions));
  const [isOpen, setIsOpen] = useState(false);
  const { validateIDBG, isIDBGValid, idbgErrMSG } = useValidateIDBG();
  const { validateServer, isServerValid, validateServerErrorMSG } =
    useValidateServer();
  const { validateAccount, isValidAccount, isDuplicate, checkAccountErrorMSG } =
    useValidateAccount();
  const canSubmit =
    isIDBGValid &&
    isServerValid &&
    isValidAccount &&
    areAllElementsNumbers(pin);

  const formData = {
    guiname: idbg,
    pincode: Object.values(pin).join(""),
    version: selectedVersion,
    server_url: serverURL,
    account_name: account,
  };
  const handlePrivateKey = (e) => {
    setPrivateKey(e.target.value);
  };
  const handleIDBGChange = (e) => {
    setIdbg(e.target.value);
  };
  useEffect(() => {
    if (!idbg) return;
    const debouncedValidate = debounce(
      () => validateIDBG(idbg, serverURL),
      500,
    );

    debouncedValidate();
  }, [idbg, serverURL, validateIDBG]);

  const handleServerChange = (e) => {
    setServerURL(e.target.value);
  };
  const handleAccountChange = (e) => {
    setAccount(e.target.value);
  };

  const createIDBG = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsOpen(true);
  };

  useEffect(() => {
    if (!serverURL) return;
    validateServer(serverURL);
  }, [serverURL, validateServer]);
  useEffect(() => {
    if (!account) return;
    if (!serverURL) return;
    validateAccount(account, serverURL);
  }, [account, serverURL, validateAccount]);

  useEffect(() => {
    const handleResize = () => {
      widthRef.current = window.innerWidth;
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <main className={`${styles.registerPage}`}>
      <section className={styles.registerSection}>
        <form onSubmit={createIDBG}>
          <div className="column gap-1">
            <div className={`gap-1 ${styles.welcome} column`}>
              <h2>Welcome, create your own database GUI in few clicks!</h2>
              <p>
                Let's get started. Fill out the form below to register your
                account and create your own database GUI.
              </p>
            </div>
            <div>
              <label htmlFor="idbg">
                <span>*</span>
                Account name
              </label>
              <InputField
                required
                value={idbg}
                onChange={handleIDBGChange}
                name="idbg"
                type="text"
                placeholder="Enter your account name"
                isValid={isIDBGValid}
              />
              <ErrorMessage value={idbgErrMSG} />
            </div>
            <div>
              <label htmlFor="server-url">
                <span>*</span>
                Inery Node URL
              </label>
              <div data-tooltip={serverUrlInputFieldTooltip}>
                <InputField
                  value={serverURL}
                  onChange={handleServerChange}
                  name="server-url"
                  type="url"
                  placeholder="Enter your server URL"
                  isValid={true}
                />
              </div>
              <ErrorMessage value={validateServerErrorMSG} />
            </div>
            <div>
              <label htmlFor="server-url">
                <span>*</span>
                Database Name
              </label>
              <InputField
                value={account}
                onChange={handleAccountChange}
                type="text"
                placeholder="Enter your database name"
                isValid={isValidAccount}
              />
              <ErrorMessage value={checkAccountErrorMSG} />
            </div>
            <div>
              <label htmlFor="version">Version</label>
              <div>
                <Select
                  value={selectedVersion}
                  onChange={(e) => setSelectedVersion(e.target.value)}
                  name="IDBGv"
                  id="IDBGv"
                >
                  {versions.map((version) => (
                    <Option key={version} value={version}>
                      {version}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
            <div>
              <label htmlFor="pin-code">
                <span>*</span>
                PIN code
              </label>
              <Pin value={pin} onChange={setPin} />
              <div className={`${styles.prompt} gap-1 column`}>
                <p>
                  Information that is going to tell the user that it’s PIN Code
                  will be encrypted and well protected.
                </p>
                <p>
                  Simplify your database management with IneryDB GUI. Check the
                  box to install.
                </p>
              </div>
            </div>
            <div>
              <div className={`column gap-05`}>
                <Button disabled={!canSubmit} type="submit" expand="full">
                  Create Account
                </Button>
                <div className="row justify-center">
                  <p>Already have an account?&nbsp; </p>
                  <Link to="/login">Log In</Link>
                </div>
              </div>
            </div>
          </div>
        </form>
      </section>
      <SideImageSection className={styles.sideImageSection} />
      <RegisterModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        accountExists={isDuplicate}
        formData={formData}
        dark={dark}
        widthRef={widthRef}
      />
      <AccountExists
        openModal={isDuplicate}
        handlePrivateKey={handlePrivateKey}
        privateKey={privateKey}
        account={account}
        handleAccountChange={handleAccountChange}
      />
    </main>
  );
};

export default Register;
